import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {SVGArrowRight, SVGMessage, SVGHotel, SVGSparkle, SVGSearch, SVGCar, SVGMap, SVGPlane, SVGBrain, SVGTrendingDown, SVGBell, SVGZap} from "../assets/PackageSVG";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import styles from "./AboutPage.module.scss";

function useReveal() {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add(styles.visible);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

type RevealSectionProps = {
  className?: string;
  children: ReactNode;
};

function RevealSection({ className, children }: RevealSectionProps) {
  const ref = useReveal();
  return (
    <section ref={ref} className={`${styles.reveal} ${className || ""}`}>
      {children}
    </section>
  );
}

type StepItem = {
  num: string;
  icon: ReactNode;
  title: string;
  desc: string;
};

const STEPS: StepItem[] = [
  {
    num: "01",
    icon: <SVGMessage width="18" height="21" stroke="#2563eb" />,
    title: "Tell Adiona what you want",
    desc: "Describe your trip in plain language. Weekend getaway, family vacation, business trip - just say it.",
  },
  {
    num: "02",
    icon: <SVGSearch width="18" height="21" stroke="#2563eb" />,
    title: "AI finds the best options",
    desc: "Flights, hotels, car rentals, tours - compared instantly across providers.",
  },
  {
    num: "03",
    icon: <SVGSparkle width="18" height="21" stroke="#2563eb" />,
    title: "Refine in conversation",
    desc: "Adjust dates, compare alternatives, ask questions. Like talking to a brilliant travel agent.",
  },
  {
    num: "04",
    icon: <SVGZap width="18" height="21" stroke="#2563eb" />,
    title: "Book in one flow",
    desc: "Payment and confirmation without leaving the chat. Your entire trip, handled.",
  },
];

type CoverageItem = {
  icon: ReactNode;
  title: string;
  desc: string;
};

const COVERAGE: CoverageItem[] = [
  {
    icon: <SVGHotel width="18" height="21" stroke="#2563eb" />,
    title: "Hotels",
    desc: "From boutique stays to global chains - the best rate for your plan.",
  },
  {
    icon: <SVGPlane width="18" height="21" stroke="#2563eb" />,
    title: "Flights",
    desc: "Every airline, every route. Real-time pricing across carriers.",
  },
  {
    icon: <SVGCar width="18" height="21" stroke="#2563eb" />,
    title: "Car Rentals",
    desc: "Pick up anywhere, drop off anywhere. Flexible plans at competitive rates.",
  },
  {
    icon: <SVGMap width="18" height="21" stroke="#2563eb" />,
    title: "Guided Tours",
    desc: "Local experiences and curated activities in each destination.",
  },
];

type TechBullet = {
  icon: ReactNode;
  text: string;
};

const TECH_BULLETS: TechBullet[] = [
  {
    icon: <SVGBrain width="18" height="21" stroke="#2563eb" />,
    text: "Natural language understanding that captures your real intent",
  },
  {
    icon: <SVGSparkle width="18" height="21" stroke="#2563eb" />,
    text: "Preference learning that improves with every trip you plan",
  },
  {
    icon: <SVGTrendingDown width="18" height="21" stroke="#2563eb"/>,
    text: "Real-time price monitoring across thousands of sources",
  },
  {
    icon: <SVGBell width="18" height="21" stroke="#2563eb" />,
    text: "Proactive deal alerts when prices drop after you book",
  },
];

const PARTNERS = ["Amadeus", "Hotelbeds", "Agoda", "Stripe", "Apple Pay", "Google Pay"];

export const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className={styles.aboutPage}>
      <Header />

      <main className={styles.content}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <h1 className={styles.heroHeadline}>We built the travel partner we always wanted.</h1>
            <p className={styles.heroSub}>
              One conversation. Flights, hotels, activities, and everything in between - planned, compared, and booked.
            </p>
          </div>
          <div className={styles.heroFade} />
        </section>

        <RevealSection className={styles.mission}>
          <div className={styles.missionInner}>
            <div className={styles.missionLeft}>
              <p className={styles.missionPull}>1 conversation. Your perfect trip.</p>
            </div>
            <div className={styles.missionRight}>
              <p>Planning a trip used to mean juggling tabs, apps, and spreadsheets. We thought that was broken.</p>
              <p>
                Adiona replaces fragmented search-compare-book cycles with a single AI-powered conversation. Describe what
                you want, and Adiona handles the rest - from first idea to final confirmation.
              </p>
              <p>No noise. No decision fatigue. Just the trip you wanted, planned in seconds.</p>
            </div>
          </div>
        </RevealSection>

        <RevealSection className={styles.steps}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>How it works</h2>
            <p className={styles.sectionDesc}>From idea to itinerary in four simple steps.</p>
          </div>
          <div className={styles.stepsGrid}>
            {STEPS.map((s) => {
              return (
              <div key={s.num} className={styles.step}>
                <span className={styles.stepNum}>{s.num}</span>
                <div className={styles.stepIco}>
                  {s.icon}
                </div>
                <h3 className={styles.stepTitle}>{s.title}</h3>
                <p className={styles.stepDesc}>{s.desc}</p>
              </div>
              );
            })}
          </div>
        </RevealSection>

        <RevealSection className={styles.coverage}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>What Adiona covers</h2>
            <p className={styles.sectionDesc}>Everything you need for a complete trip, in one place.</p>
          </div>
          <div className={styles.coverageGrid}>
            {COVERAGE.map((c, i) => {
              return (
              <div key={i} className={styles.coverageCard}>
                <div className={styles.coverageIco}>
                  {c.icon}
                </div>
                <h3 className={styles.coverageTitle}>{c.title}</h3>
                <p className={styles.coverageDesc}>{c.desc}</p>
              </div>
              );
            })}
          </div>
        </RevealSection>

        <RevealSection className={styles.tech}>
          <div className={styles.techInner}>
            <div className={styles.techLeft}>
              <h2 className={styles.techHeadline}>Intelligent by design</h2>
              <p className={styles.techSub}>
                Built on models that understand travel the way you think about it - not keyword matching, real
                comprehension.
              </p>
            </div>
            <div className={styles.techRight}>
              {TECH_BULLETS.map((b, i) => {
                return (
                <div key={i} className={styles.techBullet}>
                  <div className={styles.techBulletIco}>
                    {b.icon}
                  </div>
                  <span>{b.text}</span>
                </div>
                );
              })}
            </div>
          </div>
        </RevealSection>

        <RevealSection className={styles.integrations}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Powered by leading travel APIs</h2>
            <p className={styles.sectionDesc}>Direct agreements with carriers, hotels, and guides coming soon.</p>
          </div>
          <div className={styles.partners}>
            {PARTNERS.map((p) => (
              <span key={p} className={styles.partnerPill}>
                {p}
              </span>
            ))}
          </div>
        </RevealSection>

        <RevealSection className={styles.manifesto}>
          <h2 className={styles.manifestoHeadline}>Travel is personal. Your platform should be too.</h2>
          <p className={styles.manifestoSub}>
            We do not believe in one-size-fits-all itineraries. Adiona learns how you travel, what you value, and what
            makes a trip feel like yours.
          </p>
          <button className={styles.manifestoBtn} onClick={() => navigate("/chat")}>
            Start planning <SVGArrowRight width="18" height="21" stroke="#2563eb" />
          </button>
        </RevealSection>
      </main>

      <Footer />
    </div>
  );
};
